import {
  createSlice,
  createSelector
} from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import { CompanyListRef, ICompanyList } from '../types/common';
import { companyApi } from '../../../services/company';
import { ICompanyListModalParams } from '../types/companyList';


export interface ICompanyListsState {
  companyLists: {
    byId: Record<CompanyListRef, ICompanyList>;
    allIds: CompanyListRef[];
  },
  companyListModalParams: ICompanyListModalParams | undefined,
};

const initialState: ICompanyListsState = {
  companyLists: {
    byId: {},
    allIds: [],
  },
  companyListModalParams: undefined
};

export const companyLists = createSlice({
  name: 'companyLists',
  initialState,
  reducers: {
    createCompanyList: (state, action: PayloadAction<ICompanyList>) => {
      const ref = action.payload.ref;
      state.companyLists.allIds.push(ref);
      state.companyLists.byId[ref] = action.payload;
    },
    updateCompanyList: (state, action: PayloadAction<ICompanyList>) => {
      const ref = action.payload.ref;
      state.companyLists.byId[ref] = action.payload;
    },
    updateCompanyListName: (state, action: PayloadAction<{
      ref: ICompanyList['ref'],
      name: ICompanyList['name'],
    }>) => {
      const companyList = state.companyLists.byId[action.payload.ref];
      if (companyList) {
        companyList.name = action.payload.name;
      }
    },
    removeCompanyListById: (state, action: PayloadAction<CompanyListRef>) => {
      const ref = action.payload;
      delete state.companyLists.byId[action.payload];
      state.companyLists.allIds = state.companyLists.allIds.filter(id => id !== ref);
    },
    updateCompanyListRequirements: (state, action: PayloadAction<{
      ref: ICompanyList['ref'],
      requirements: ICompanyList['requirements']
    }>) => {
      const ref = action.payload.ref;
      const requirements = action.payload.requirements;

      state.companyLists.byId[ref]['requirements'] = requirements;
    },
    addCompanyListShortlists: (state, action: PayloadAction<{
      ref: ICompanyList['ref'],
      shortlist: ICompanyList['shortlist']
    }>) => {
      const ref = action.payload.ref;
      if (!state.companyLists.byId[ref]['shortlist']) {
        state.companyLists.byId[ref]['shortlist'] = [];
      }
      state.companyLists.byId[ref]['shortlist'].unshift(...(action.payload.shortlist ?? []));
    },
    removeCompanyListShortlists: (state, action: PayloadAction<{
      ref: ICompanyList['ref'],
      shortlist: ICompanyList['shortlist']
    }>) => {
      const ref = action.payload.ref;
      const refsToRemove = new Set(action.payload?.shortlist?.map(s => s.company_id) ?? []);

      if (!state.companyLists.byId[ref].shortlist) {
        state.companyLists.byId[ref].shortlist = [];
      }
      const remainingCompanyIds = state.companyLists.byId[ref].shortlist.filter(
        item => !refsToRemove.has(item.company_id)
      );

      state.companyLists.byId[ref].shortlist = remainingCompanyIds;
    },
    addCompanyLists: (state, action: PayloadAction<ICompanyList[]>) => {
      const allCompanyListRefs: ICompanyListsState['companyLists']['allIds'] = [];
      const companyListByIds: ICompanyListsState['companyLists']['byId'] = {};
      action.payload.forEach((companyList) => {
        allCompanyListRefs.push(companyList.ref);
        companyListByIds[companyList.ref] = companyList;
      });

      state.companyLists.allIds = allCompanyListRefs;
      state.companyLists.byId = companyListByIds;
    },
    addCompanyList: (state, action: PayloadAction<ICompanyList>) => {
      const ref = action.payload.ref;

      if (!state.companyLists.allIds.includes(ref)) {
        state.companyLists.allIds.push(ref);
      }

      state.companyLists.byId[ref] = action.payload;
    },
    setCompanyListModalParams(state, action: PayloadAction<ICompanyListModalParams | undefined>) {
      state.companyListModalParams = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(companyApi.endpoints.getCompanyListByRefs.matchFulfilled, (state, action) => {
        // handles case if the ref provided in the url is not valid and api doesnt return any company builder list obj for it
        if (action.payload.data[0]) {
          const addCompanyListAction = addCompanyList(action.payload.data[0]);
          companyLists.caseReducers.addCompanyList(state, addCompanyListAction);
        }
      })
  }
});

// Accessors
export const selectAllCompanyLists = createSelector(
  (state: RootState) => state.companyLists.companyLists.byId,
  (byId) => {
    return Object.values(byId);
  }
);

export const selectCompanyListByRef = createSelector(
  (state: RootState) => state.companyLists.companyLists.byId,
  (_: RootState, ref: CompanyListRef | undefined) => ref,
  (byId, ref) => ref ? byId[ref] : undefined,
);

export const selectRequirementsByCompanyListRef = (
  companyListRef: CompanyListRef | undefined,
) => createSelector(
  (state: RootState) => selectCompanyListByRef(state, companyListRef),
  (companyList) => {
    if (!companyList) return undefined;
    return companyList.requirements ?? [];
  }
);

export const isShortlistedInCompanyList = (
  companyListRef: CompanyListRef | undefined,
  companyId: string,
) => createSelector(
  (state: RootState) => selectCompanyListByRef(state, companyListRef),
  (companyList) => {
    if (!companyList) return false;
    return companyList.shortlist?.some(item => item.company_id === companyId) ?? [];
  }
);

export const {
  createCompanyList,
  updateCompanyList,
  updateCompanyListName,
  removeCompanyListById,
  updateCompanyListRequirements,
  addCompanyListShortlists,
  removeCompanyListShortlists,
  addCompanyList,
  setCompanyListModalParams,
} = companyLists.actions;

export default companyLists.reducer;
